import React, { useState, useEffect } from 'react';
import {
  Pane,
  Heading,
  Button,
  toaster,
  majorScale,
  TextInput,
  Text,
  Table,
  Dialog,
  IconButton,
  EditIcon,
  TrashIcon,
  EyeOpenIcon,
  Select,
} from 'evergreen-ui';
import { jwtDecode } from 'jwt-decode'; // Import jwtDecode

function ShippingAddressForm({ onAddressAdded }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    pickupPoint: '',
    countyName: '',
    phoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [viewingAddress, setViewingAddress] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);

  // Fetch addresses and locations on component mount
  useEffect(() => {
    fetchAddresses();
    fetchLocations();
  }, []);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toaster.danger('Please log in to view addresses');
        return;
      }
      const decoded = jwtDecode(token);
      const userId = decoded.id; // Adjust based on your token structure
  
      // Update the URL to match the backend route
      const response = await fetch(`http://localhost:5000/shipping-address/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch addresses');
      const data = await response.json();
      setAddresses(data.data); // Update to access 'data' property from backend response
    } catch (error) {
      toaster.danger('Error fetching addresses: ' + error.message);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch('http://localhost:5000/locations');
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      toaster.danger('Error fetching locations: ' + error.message);
    }
  };

  // Update pickup points when countyName changes
  useEffect(() => {
    if (formData.countyName) {
      const filteredPickupPoints = locations
        .filter((location) => location.countyName === formData.countyName)
        .map((location) => location.pickupPoint);
      setPickupPoints(filteredPickupPoints);
      setFormData((prev) => {
        if (!filteredPickupPoints.includes(prev.pickupPoint)) {
          return { ...prev, pickupPoint: '' };
        }
        return prev;
      });
    } else {
      setPickupPoints([]);
      setFormData((prev) => ({ ...prev, pickupPoint: '' }));
    }
  }, [formData.countyName, locations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate required fields
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.email ||
        !formData.pickupPoint ||
        !formData.countyName ||
        !formData.phoneNumber
      ) {
        throw new Error('All fields are required');
      }

      // Get userId from JWT token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be logged in to save an address');
      }
      const decoded = jwtDecode(token);
      const userId = decoded.id; // Adjust based on your token structure (e.g., 'id', 'userId')

      const payload = { ...formData, userId }; // Include userId in the payload

      const url = editingAddress
        ? `http://localhost:5000/shipping-address/${editingAddress.id}`
        : 'http://localhost:5000/shipping-address';
      const method = editingAddress ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Include token for authentication
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save shipping address');
      }

      toaster.success(
        editingAddress
          ? 'Address updated successfully!'
          : 'Address saved successfully!'
      );
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        pickupPoint: '',
        countyName: '',
        phoneNumber: '',
      });
      setEditingAddress(null);
      fetchAddresses();
      if (onAddressAdded && !editingAddress) {
        onAddressAdded();
      }
    } catch (error) {
      toaster.danger('Error saving address: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      firstName: address.firstName,
      lastName: address.lastName,
      email: address.email,
      pickupPoint: address.pickupPoint,
      countyName: address.countyName,
      phoneNumber: address.phoneNumber,
    });
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be logged in to delete an address');
      }

      const response = await fetch(`http://localhost:5000/shipping-address/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete address');
      toaster.success('Address deleted successfully!');
      fetchAddresses();
    } catch (error) {
      toaster.danger('Error deleting address: ' + error.message);
    }
  };

  const handleView = (address) => {
    setViewingAddress(address);
    setIsDialogOpen(true);
  };

  // Get unique county names
  const countyNames = [...new Set(locations.map((location) => location.countyName))];

  return (
    <Pane
      background="white"
      padding={majorScale(3)}
      borderRadius={8}
      elevation={1}
      marginBottom={majorScale(3)}
      width="100%"
      maxWidth={600}
    >
      <Heading size={600} marginBottom={majorScale(2)}>
        {editingAddress ? 'Edit Shipping Address' : 'Add Shipping Address'}
      </Heading>
      <form onSubmit={handleSubmit}>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            First Name <Text color="red">*</Text>
          </Text>
          <TextInput
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="e.g., John"
            width="100%"
            required
          />
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Last Name <Text color="red">*</Text>
          </Text>
          <TextInput
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="e.g., Doe"
            width="100%"
            required
          />
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Email <Text color="red">*</Text>
          </Text>
          <TextInput
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="e.g., john.doe@example.com"
            width="100%"
            required
          />
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            County Name <Text color="red">*</Text>
          </Text>
          <Select
            name="countyName"
            value={formData.countyName}
            onChange={handleChange}
            width="100%"
            required
          >
            <option value="" disabled>
              Select a county
            </option>
            {countyNames.map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </Select>
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Pickup Point <Text color="red">*</Text>
          </Text>
          <Select
            name="pickupPoint"
            value={formData.pickupPoint}
            onChange={handleChange}
            width="100%"
            required
            disabled={!formData.countyName}
          >
            <option value="" disabled>
              Select a pickup point
            </option>
            {pickupPoints.map((point) => (
              <option key={point} value={point}>
                {point}
              </option>
            ))}
          </Select>
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Phone Number <Text color="red">*</Text>
          </Text>
          <TextInput
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="e.g., 07XXXXXXXX"
            width="100%"
            required
          />
        </Pane>
        <Pane marginTop={majorScale(2)} display="flex" gap={majorScale(2)}>
          <Button
            appearance="primary"
            type="submit"
            isLoading={isLoading}
            disabled={
              !formData.firstName ||
              !formData.lastName ||
              !formData.email ||
              !formData.pickupPoint ||
              !formData.countyName ||
              !formData.phoneNumber
            }
          >
            {editingAddress ? 'Update Address' : 'Save Address'}
          </Button>
          {editingAddress && (
            <Button
              appearance="default"
              onClick={() => {
                setEditingAddress(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  pickupPoint: '',
                  countyName: '',
                  phoneNumber: '',
                });
              }}
            >
              Cancel
            </Button>
          )}
        </Pane>
      </form>

      {/* Address List */}
      <Pane marginTop={majorScale(4)}>
        <Heading size={500} marginBottom={majorScale(2)}>
          Saved Addresses
        </Heading>
        {addresses.length === 0 ? (
          <Text>No addresses saved yet.</Text>
        ) : (
          <Table>
            <Table.Head>
              <Table.TextHeaderCell>Name</Table.TextHeaderCell>
              <Table.TextHeaderCell>Details</Table.TextHeaderCell>
              <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {addresses.map((address) => (
                <Table.Row key={address.id}>
                  <Table.TextCell>
                    {address.firstName} {address.lastName}
                  </Table.TextCell>
                  <Table.TextCell>
                    {address.pickupPoint}, {address.countyName}
                  </Table.TextCell>
                  <Table.Cell>
                    <IconButton
                      icon={EyeOpenIcon}
                      marginRight={majorScale(1)}
                      onClick={() => handleView(address)}
                      title="View"
                    />
                    <IconButton
                      icon={EditIcon}
                      marginRight={majorScale(1)}
                      onClick={() => handleEdit(address)}
                      title="Edit"
                    />
                    <IconButton
                      icon={TrashIcon}
                      intent="danger"
                      onClick={() => handleDelete(address.id)}
                      title="Delete"
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Pane>

      {/* View Address Dialog */}
      <Dialog
        isShown={isDialogOpen}
        title="Address Details"
        onCloseComplete={() => setIsDialogOpen(false)}
        hasFooter={false}
      >
        {viewingAddress && (
          <Pane>
            <Text display="block" marginBottom={majorScale(1)}>
              <strong>Name:</strong> {viewingAddress.firstName}{' '}
              {viewingAddress.lastName}
            </Text>
            <Text display="block" marginBottom={majorScale(1)}>
              <strong>Email:</strong> {viewingAddress.email}
            </Text>
            <Text display="block" marginBottom={majorScale(1)}>
              <strong>Pickup Point:</strong> {viewingAddress.pickupPoint}
            </Text>
            <Text display="block" marginBottom={majorScale(1)}>
              <strong>County Name:</strong> {viewingAddress.countyName}
            </Text>
            <Text display="block" marginBottom={majorScale(1)}>
              <strong>Phone Number:</strong> {viewingAddress.phoneNumber}
            </Text>
          </Pane>
        )}
      </Dialog>
    </Pane>
  );
}

export default ShippingAddressForm;